import {
  Middleware, useSWRConfig, Arguments, unstable_serialize,
} from 'swr';
import { useEffect, useRef } from 'react';


/**
 * @description SWR Middleware that populates the cache with initial data from a server-side request. We prevent the
 * initial request by resolving a Promise with the server-side data instead of fetching it.
 *
 * 1. The process only happens at hook mount, we do NOT prevent further mutations even if they have the same cache key
 * (we use useRef, we don't trigger effects).
 * 2. The process is carried out only for the key that the hook writes to the mount cache. As an example, we prevent the
 * request and write directly into the cache only the existing key at the time the page is accessed,
 * i.e.: /v3/talent-search&page=2 but not for /v3/talent-search&page=2&limit=12 or /v3/talent -search&page=3.
 * As the middleware runs at each SWR request, a way of unambiguous identification must be found; a key is always the
 * first as long as it has not been written in the return cache (key, fetcher, config). It remains to be seen how SWR
 * writes a series of keys for infinity (in the cache), if it has another identifier.
 * 3. When *{revalidateOnMount: true}*, we only revalidate the sibling keys (i.e., the same URL & params only the page is different),
 * not the original key which gets its data from the server-side request.
 *
 * **Parameters:**
 * - **keyOnMount** - the initial key that the hook uses on mounting.
 *
 * **Returns:**
 * - the SWR hook with the server side data if available.
 *
 * @example
 * // Usage in middlewares for SWR infinite:
 * serverDataInfinite(keyOnMount)
 */
export const serverDataInfinite = (keyOnMount: Arguments): Middleware => (useSWRNext) => (key, fetcher, config) => {
  // We use the global 'mutate' function to have access to the first key in the series.
  const { mutate } = useSWRConfig();
  const firstKeyInSeries = unstable_serialize(keyOnMount);

  // Store hook mount status
  const alreadyMounted = useRef(false);

  // Data from the server
  const baseServerData = config?.fallbackData;

  // Because in SWR infinite we need to put the server data into an array, we need to extract it.
  // The data is always an array, so we take the first element.
  const serverData = baseServerData && Array.isArray(baseServerData) ? baseServerData[0] as typeof baseServerData : baseServerData;

  // We use the page key, not the infinite series key.
  useEffect(() => {
    if (serverData && !alreadyMounted.current) {
      alreadyMounted.current = true;
      void mutate(firstKeyInSeries, serverData, {
        // Write the data in the cache without triggering a revalidation (no extra fetch)
        optimisticData: true,
        populateCache: true,
        revalidate: false,
      });
    }
  }, [serverData, mutate, firstKeyInSeries]);


  // Remove the server data (fallbackData) from the cache if we already mounted the component.
  // This is needed because we want to prevent using the server data from the same hook used in multiple components.
  // ********************************************
  return useSWRNext(key, fetcher, {
    ...config,
    fallbackData: alreadyMounted.current ? undefined : baseServerData,
  });
};
